import { type Primitive } from '../../../features/ViolationsTab/utils/utils-violationslist';
import {
  type ActiveAdvancedFilterState,
  type AdvancedFilterState,
} from '../../../shared/types';
import { type AppliedColumnFilters } from '../AuditLogColumnFilter';
import { type TimelineData } from '../TimeLineBlock';

export enum AuditLogType {
  API_SLICE_NAME = 'aag/auditLogApi',
}

export enum ReviewState {
  REVIEW_COMPLETED = 'REVIEW_COMPLETED',
  INGESTION_IN_PROGRESS = 'INGESTION_IN_PROGRESS',
  INGESTION_NOT_STARTED = 'INGESTION_NOT_STARTED',
  ACCESS_DENIED = 'ACCESS_DENIED',
  READY_FOR_REVIEW = 'READY_FOR_REVIEW',
}

// API Request Types
export interface AuditLogSearchRequest extends Record<string, unknown> {
  searchAfter: (string | number)[] | null;
  pageSize: number;
  requestAccessKey: number | string;
  filters: {
    searchText: string;
    logTypes: string[];
    changeIndicators: string[];
    entitlementDisplayNames: string[];
    severities: string[];
    auditClasses: string[];
    entitlementDeviations: boolean[]; // expects [true, false] values
    functionNames: string[];
    auditLogStatuses: string[];
    startDate: string | null;
    endDate: string | null;
    insightIds: string[];
  };
}

// API Response Types
export interface RequestMetadata {
  /** API `data.requestType` (e.g. `FF_ID`, `FF_ROLE`, `FF_ROLE|FF_ID`). */
  requestType: string;
  /** API `requestDetail.requestHeaderDisplayName` when present (works for both FF_ID and FF_ROLE). */
  requestHeaderDisplayName: string;
  /** Populated for FF_ROLE-style `requestDetail`; empty for FF_ID-only. */
  ffRoleName: string;
  requestId: number;
  requestAccessKey: number | string;
  taskId: number;
  connectiontype: string | null;
  applicationType: string | null;
  requestorUserDisplayName: string;
  /** Populated for FF_ID-style `requestDetail`; empty for FF_ROLE-only. */
  ffid: string;
  ticketNumber: string;
  businessJustification: string;
  reasonCode: string;
  requestStartedAt: string;
  requestEndedAt: string;
  sourceTimeZone: string;
  requestedEntitlementDisplayNames?: string[];
  endpointKey: number;
  endpointDisplayName: string;
  endpointLogo?: string;
  approverUserDisplayName: string | null;
  readOnly: boolean;
}

export interface AuditLogSearchApiResponse {
  status: number;
  message: string;
  data: {
    searchAfter: (string | number)[] | null;
    pageSize: number;
    results: TimelineData[];
    displayRecords: number;
    totalRecords: number;
  };
}

// Transform response type for the hook
export interface AuditLogSearchResponse {
  data: TimelineData[];
  totalRecords: number;
  searchAfter: (string | number)[] | null;
}

// Request + Review details API types
export interface ReviewActivitySummary {
  flagged: number;
  commented: number;
  resolved: number;
}

export interface ReviewDetailInfo {
  reviewComment: string;
  reviewCompletedByDisplayName: string;
  reviewCompletedAt: string;
  status: string;
  activitySummary: ReviewActivitySummary;
}

/**
 * `requestReviewDetails.requestDetail` — shape differs by request type:
 * - **FF_ID**: `ffid`, endpoint fields, optional `launchDetail`
 * - **FF_ROLE**: `ffRoleName`; typically no `ffid` / endpoint / launches
 */
export interface RequestDetailFromApi {
  requestId: number;
  requestAccessKey: number | string;
  taskId?: number;
  requestorUserDisplayName?: string;
  /** FF_ID requests */
  ffid?: string;
  /** FF_ROLE requests */
  ffRoleName?: string;
  connectionType?: string | null;
  applicationType?: string | null;
  ticketNumber?: string | null;
  businessJustification?: string;
  reasonCode?: string;
  requestStartedAt?: string;
  requestEndedAt?: string | null;
  sourceTimeZone?: string | null;
  requestedEntitlementDisplayNames?: string[];
  endpointKey?: number;
  endpointDisplayName?: string;
  endpointLogo?: string;
  endpointName?: string;
  approverUserDisplayName?: string | null;
  requestHeaderDisplayName?: string;
}

export interface LaunchDetail {
  launchId: number;
  launchStartDate: string;
  launchEndDate: string;
}

export interface RequestReviewDetailsApiResponse {
  status: number;
  message: string;
  data: {
    state: ReviewState;
    readOnly: boolean;
    requestType?: string;
    requestDetail?: RequestDetailFromApi & {
      launchDetail?: LaunchDetail[];
    };
    reviewDetail?: ReviewDetailInfo;
    isActivityLogEnabled?: boolean;
    extraAttribute?: Record<string, unknown>;
  };
}

export interface RequestReviewDetailsTransformed {
  requestMetadata: RequestMetadata;
  isReadOnly: boolean;
  reviewSummary: ReviewDetailInfo;
  state: ReviewState;
  launchDetail: LaunchDetail[];
}

export interface FunctionRec {
  functionKey?: Primitive;
  id?: Primitive;
  functionName?: Primitive;
}

export interface FunctionOption {
  label: string;
  value: string;
  isChecked: boolean;
}

// Component Props Interface
export interface AuditLogViewerBodyProps {
  className?: string;
  isInsightsOpen?: boolean;
  onInsightsToggle?: () => void;
  onTimelineItemClick?: (
    item: TimelineData,
    tab?: 'summary' | 'review-activity'
  ) => void;
  /** Clicking the timestamp text should open/select a recording session. */
  onTimelineTimestampClick?: (item: TimelineData) => void;
  selectedTimelineItemId?: string;
  isDetailPanelOpen?: boolean;
  isRecordingOpen?: boolean;
  onRecordingToggle?: () => void;
  // Lifted callback to close the detail panel (replaces global event)
  closeDetailPanel?: () => void;
  showInsightsButton?: boolean;
  appliedColumnFilters?: AppliedColumnFilters;
  onColumnFiltersChange?: (filters: AppliedColumnFilters) => void;
  // Timeline data
  timelineData?: TimelineData[];
  // Advanced filters props
  advancedFilters?: AdvancedFilterState[];
  activeAdvancedFilters?: ActiveAdvancedFilterState[];
  onAdvancedFiltersChange?: (filters: ActiveAdvancedFilterState[]) => void;
  // Carry-over filters support (from Transaction Overview)
  initialActiveFilters?: ActiveAdvancedFilterState[];
  // Clear carry-over state callback (so it doesn't re-apply after Clear)
  onClearCarryOverEntitlement?: () => void;
}

// ECM v6: Get Entitlements response type (subset)
export interface GetEntitlementsApiResponse {
  status?: number;
  errorCode?: string;
  entitlementsCount?: number;
  Entitlementdetails?: {
    entitlement_valuekey?: string | number;
    entitlement_value?: string;
  }[];
}

// Audit columns API types
export interface AuditColumnDefApiResponse {
  id: string;
  localizationKey: string;
  isDefault: boolean;
}

export interface AuditFilterDef {
  id: string;
  localizationKey: string;
  isDefault: boolean;
}

export interface GetAuditColumnsApiResponse {
  status?: number;
  message?: string;
  data?: {
    requestHeader?: AuditFilterDef[];
    requestDetail?: AuditFilterDef[];
    reviewDetail?: AuditFilterDef[];
    auditLogFilters?: AuditFilterDef[];
    entitlementFilters?: AuditFilterDef[];
    securityAuditLog?: AuditColumnDefApiResponse[];
    changeAuditLog?: AuditColumnDefApiResponse[];
    tcodeAuditLog?: AuditColumnDefApiResponse[];
    systemAuditLog?: AuditColumnDefApiResponse[];
  };
}

// Review actions API types
export type ReviewStatus = 'FLAGGED' | 'RESOLVED' | 'COMMENTED';

export interface ReviewActivity {
  status: ReviewStatus;
  user: string;
  comment: string;
  updatedAt: string | number | Date;
}

export interface PostReviewRequestBody {
  comment: string;
  status: ReviewStatus;
  actionedBy: string;
}

export interface PostReviewRequest {
  uniqueLogId: string | number;
  body: PostReviewRequestBody;
}

export interface PostReviewApiResponse {
  status: number;
  message: string;
  data?: {
    reviewActivities?: ReviewActivity[];
  };
}

export interface AuditFiltersOptionsApiResponse {
  status?: number;
  message?: string;
  data?: Record<
    string,
    {
      options?: { label: string; value: unknown }[];
    }
  >;
}

// Transaction Overview: Search Entitlements API types
export interface SearchEntitlementsItem {
  tcodeId: number;
  tcodeValue: string;
  tcodeDisplayName: string;
  tcodeDescription: string;
  tcodeSoxCritical: string;
  tcodeSysCritical: string;
  tcodeFunctionArea: string[];
  tcodeBusprocsArea: string[];
  tcodeDeviation: boolean;
  tcodeUsageCount: number;
  tcodeUsageDuration: number;
}

export interface SearchEntitlementsApiResponse {
  status: number;
  message: string;
  data: {
    afterKey: number | string | null;
    pageSize: number;
    results: EntitlementsListItemFromApi[];
  };
}

export interface SearchEntitlementsRequest {
  requestAccessKey: number | string;
  filters: {
    logTypes?: string[];
    entitlementValues?: string[];
    severities?: string[];
    entitlementDeviations?: boolean[];
    functionNames?: string[];
    searchText?: string;
  };
  afterKey: null | number | string | Record<string, string>;
  pageSize: number;
  // pagination and sort removed as per latest API contract
}

// New response shape for Entitlements search
export interface EntitlementsListItemFromApi {
  entitlementValueKey: number | string;
  entitlementValue: string;
  entitlementDisplayName: string | null;
  entitlementDescription: string | null;
  entitlementSoxSeverity: string | number;
  entitlementSysSeverity: string | number;
  functionNames: string[] | null;
  businessProcessNames: string[] | null;
  entitlementDeviation: boolean | string;
  entitlementUsageCount: number;
  entitlementUsageDuration: number | string;
}

export interface EntitlementsSearchResponse {
  afterKey: number | string | null;
  data: EntitlementsListItemFromApi[];
}

// Insights API types
export interface InsightItemApi {
  insightId: string | number;
  description: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  entitlements?: {
    entitlementDisplayName: string;
    entitlementDescription?: string | null;
    entitlementUsage?: number | string;
  }[];
}

export interface InsightsApiResponse {
  status: number;
  message: string;
  data: {
    risk?: {
      riskScore?: string | number;
      riskSeverity?: string;
    };
    results?: InsightItemApi[];
  };
}

// Complete Review API types
export interface CompleteReviewRequestBody {
  reviewComment: string;
}

export interface CompleteReviewRequest {
  requestAccessKey: string | number;
  body: CompleteReviewRequestBody;
}

export interface CompleteReviewApiResponse {
  status: number;
  message: string;
  data?: {
    success: boolean;
  };
}

export interface CompleteReviewResponse {
  success: boolean;
  message: string;
}
